<template>
    <Head :title="pageTitle" />
    <BaseLayout>
        <BaseLink href="/reflections" class="self-start body-small text-ink-muted hover:text-ink flex items-center gap-1">
            <i class="bi bi-chevron-left" aria-hidden="true"></i> {{ t("assessments.start.backToReflections") }}
        </BaseLink>

        <BasePageHeader :title="pageTitle">
            <template #subtitle>{{ subtitle }}</template>
        </BasePageHeader>

        <div v-if="loading" class="body-small text-ink-muted">{{ t("assessments.start.loading") }}</div>
        <BaseNotification v-else-if="loadError" theme="error" :text="loadError" hide-dismiss />
        <!-- The seeded template is global, so an empty list means active campaigns own every template, not that none are enabled. -->
        <BaseCard v-else-if="!templates.length" padding="md">
            <p class="body-small text-ink-muted">{{ t("reflections.start.startedFromUpcoming") }}</p>
        </BaseCard>
        <template v-else>
            <BaseCard v-if="templates.length > 1" padding="md">
                <label for="template-select" class="block">
                    <span class="block body-small-bold text-ink mb-2">{{ t("assessments.start.templateLabel") }}</span>
                    <select
                        id="template-select"
                        v-model="selectedTemplateId"
                        class="w-full px-3 py-2 border border-control-border rounded-lg outline-none focus:border-control-border-focus body-small">
                        <option v-for="templateOption in templates" :key="templateOption.id" :value="templateOption.id">{{ templateOption.name }}</option>
                    </select>
                    <span v-if="!isSelfMode && unlockNote" class="block details text-ink-muted mt-1">{{ unlockNote }}</span>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode && !existingCycle" padding="md">
                <label for="self-assess" class="flex items-start gap-3 cursor-pointer">
                    <input id="self-assess" v-model="selfAssess" type="checkbox" class="mt-1" />
                    <span>
                        <span class="block body-small-bold text-ink">{{ t("assessments.start.selfAssessLabel") }}</span>
                        <span class="block body-small text-ink-muted mt-1">{{ t("assessments.start.selfAssessDescription") }}</span>
                    </span>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode && !existingCycle" padding="md">
                <label for="personal-note" class="block">
                    <I18nT keypath="assessments.start.personalNoteLabel" tag="span" class="block body-small-bold text-ink mb-2">
                        <template #optional>
                            <span class="text-ink-muted font-normal">{{ t("assessments.start.personalNoteOptional") }}</span>
                        </template>
                    </I18nT>
                    <textarea
                        id="personal-note"
                        v-model="personalNote"
                        rows="3"
                        :placeholder="t('assessments.start.personalNotePlaceholder')"
                        class="w-full px-3 py-2 border border-control-border rounded-lg resize-none body-small focus-field"></textarea>
                </label>
            </BaseCard>

            <BaseCard v-if="!isSelfMode && !existingCycle" theme="muted" padding="md" class="flex items-start gap-3">
                <i class="bi bi-shield-check text-notification-success-text mt-1" aria-hidden="true"></i>
                <div class="body-small text-ink">
                    <div class="body-small-bold">{{ t("assessments.start.anonymityTitle") }}</div>
                    {{ t("assessments.start.anonymityBody") }}
                </div>
            </BaseCard>

            <BaseNotification v-if="error" theme="error" :text="error" hide-dismiss />

            <!-- Same rule as the hub's Start360Panel: one open cycle per template and mode, so the creation fields give way to the existing one. -->
            <div class="sr-only" role="status" aria-live="polite">{{ recoveryAnnouncement }}</div>
            <BaseCard v-if="existingCycle" theme="cream-muted" padding="md" data-testid="existing-cycle-notice">
                <div ref="existingNoticeEl" tabindex="-1" class="flex flex-col gap-3 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-control-border-focus">
                    <div>
                        <div class="body-semibold text-ink">{{ t("reflections.sixty.start.existing.title", { name: existingCycle.template_name }) }}</div>
                        <div class="body-small text-ink-muted">{{ existingCycleSummary }}</div>
                    </div>
                    <div class="flex flex-wrap items-center justify-end gap-2">
                        <BaseButton v-if="existingCycle.can_delete !== false" theme="tertiary" :disabled="deleting" @click="showDeleteConfirm = true">
                            {{ t("reflections.sixty.start.existing.deleteFresh") }}
                        </BaseButton>
                        <BaseButton theme="primary" :disabled="deleting" @click="onContinueExisting">{{ continueLabel }}</BaseButton>
                    </div>
                </div>
            </BaseCard>
            <div v-else class="flex justify-end">
                <BaseButton theme="primary" :disabled="creating" @click="onCreate">{{ createButtonLabel }}</BaseButton>
            </div>
        </template>
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="showDeleteConfirm"
        :title="deleteTitle"
        :text="deleteText"
        :confirm-label="t('common.delete')"
        confirm-theme="danger"
        :confirm-disabled="deleting"
        :cancel-disabled="deleting"
        :close-on-confirm="false"
        @confirm="onConfirmDelete" />
    <AssessmentDemoTools :bootstrap-kind="isSelfMode ? 'self' : 'sixty'" />
</template>

<script lang="ts">
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ExistingCycleSummary, StartCycleContextResponse, TemplateSummary } from "~vue/api/generated/reflections";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import AssessmentDemoTools from "~vue/pages/reflections/AssessmentDemoTools.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { formatNumber } from "~vue/utils/formatNumber";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, nextTick, onMounted, ref } from "vue";
import { I18nT, useI18n } from "vue-i18n";

const { t } = useI18n();

const isSelfMode = new URLSearchParams(globalThis.location.search).get("mode") === "self";

const templates = ref<TemplateSummary[]>([]);
const selectedTemplateId = ref<string | null>(null);
const defaultMinResponses = ref<number | null>(null);
const selectedTemplate = computed(() => templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value) ?? null);
const minResponses = computed<number | null>(() => selectedTemplate.value?.min_responses ?? defaultMinResponses.value);
const questionCount = computed<number | null>(() => {
    const selected = templates.value.find((templateOption) => templateOption.id === selectedTemplateId.value);
    return selected?.question_count ?? null;
});

const pageTitle = computed(() => (isSelfMode ? t("assessments.start.titleSelf") : t("assessments.start.title360")));

const loading = ref(true);
const loadError = ref<string | null>(null);

const selfAssess = ref(true);
// Category thresholds replace the overall minimum, so neither the subtitle nor
// the picker note can promise an unlock here. The nomination step spells the
// thresholds out instead.
const hasCategoryThresholds = computed(() => (selectedTemplate.value?.category_thresholds?.thresholds?.length ?? 0) > 0);
const unlockNote = computed(() => {
    if (hasCategoryThresholds.value || minResponses.value === null) {
        return null;
    }
    return t("assessments.start.unlockNote", { count: minResponses.value });
});

// A template always carries its question count; it is only missing before one is
// selected, and then the subtitle drops the count rather than leaving a gap.
const subtitle = computed<string>(() => {
    const count = questionCount.value;
    if (isSelfMode) {
        return count === null ? t("assessments.start.subtitle.selfNoCount") : t("assessments.start.subtitle.self", { count }, count);
    }
    if (hasCategoryThresholds.value) {
        return count === null ? t("assessments.start.subtitle.noMinimum") : t("assessments.start.subtitle.categoryThresholds", { count }, count);
    }
    if (minResponses.value === null) {
        return t("assessments.start.subtitle.noMinimum");
    }
    if (minResponses.value === 0) {
        return count === null ? t("assessments.start.subtitle.readyOnLaunchNoCount") : t("assessments.start.subtitle.readyOnLaunch", { count }, count);
    }
    const params = { minResponses: minResponses.value };
    return count === null ? t("assessments.start.subtitle.readyAfterResponsesNoCount", params) : t("assessments.start.subtitle.readyAfterResponses", { ...params, count }, count);
});

const personalNote = ref("");
const creating = ref(false);
const error = ref<string | null>(null);

const createButtonLabel = computed(() => {
    if (creating.value) {
        return t("assessments.start.creating");
    }
    return isSelfMode ? t("assessments.start.startSelfAssessment") : t("assessments.start.createCycle");
});

const existingCycles = ref<ExistingCycleSummary[]>([]);
const existingCycle = computed(() => existingCycles.value.find((cycle) => cycle.template_id === selectedTemplateId.value) ?? null);
const showDeleteConfirm = ref(false);
const deleting = ref(false);
const existingNoticeEl = ref<HTMLElement | null>(null);
const recoveryAnnouncement = ref("");

const existingCycleSummary = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return (cycle.answered_count ?? 0) > 0
            ? t("reflections.sixty.start.existing.selfProgress", { answered: formatNumber(cycle.answered_count ?? 0), total: formatNumber(cycle.question_count ?? 0) })
            : t("reflections.sixty.start.existing.selfResume");
    }
    if (cycle.state === "draft") {
        return t("reflections.sixty.start.existing.notLaunched", { count: cycle.invited_count });
    }
    const progress = t("reflections.sixty.start.existing.collecting", { invited: formatNumber(cycle.invited_count), responded: formatNumber(cycle.responded_count) });
    return cycle.can_delete === false ? t("reflections.sixty.start.existing.undeletable", { progress }) : progress;
});

const continueLabel = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return (cycle.answered_count ?? 0) > 0 ? t("reflections.sixty.start.existing.resume") : t("reflections.sixty.start.existing.continue");
    }
    return cycle.state === "draft" ? t("reflections.sixty.start.existing.continueSetup") : t("reflections.sixty.start.existing.addRaters");
});

const deleteTitle = computed(() => (existingCycle.value?.is_self_assessment_only ? t("reflections.shared.deleteCycle.titleSelf") : t("reflections.shared.deleteCycle.title360")));

const deleteText = computed(() => {
    const cycle = existingCycle.value;
    if (!cycle) {
        return "";
    }
    if (cycle.is_self_assessment_only) {
        return t("reflections.shared.deleteCycle.bodySelf");
    }
    if (cycle.state === "draft") {
        return t("reflections.shared.deleteCycle.bodyDraft");
    }
    return t("reflections.shared.deleteCycle.bodyCollecting");
});

function onContinueExisting() {
    const cycle = existingCycle.value;
    if (!cycle) {
        return;
    }
    router.visit(cycle.is_self_assessment_only ? `/reflections/360/${cycle.id}/self` : `/reflections/360/${cycle.id}/nominate`);
}

async function onConfirmDelete() {
    const cycle = existingCycle.value;
    if (!cycle || deleting.value) {
        return;
    }
    deleting.value = true;
    error.value = null;
    // 204 with no body on success, so don't ask for JSON.
    const { error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycle.id}`).delete();
    deleting.value = false;
    showDeleteConfirm.value = false;
    if (fetchError.value) {
        error.value = fetchError.value.message || t("reflections.shared.deleteCycle.failed");
        return;
    }
    existingCycles.value = existingCycles.value.filter((candidate) => candidate.id !== cycle.id);
}

async function loadStartContext(): Promise<void> {
    const startContextUrl = isSelfMode ? "/v1/reflections/start-context?mode=self" : "/v1/reflections/start-context";
    const { data, error: fetchError } = await useFetch(startContextUrl).json();
    if (fetchError.value) {
        loadError.value = (data.value as { error?: string })?.error || (isSelfMode ? t("assessments.start.loadErrorSelf") : t("assessments.start.loadError360"));
        return;
    }
    const ctx = (data.value as StartCycleContextResponse).context;
    defaultMinResponses.value = ctx.min_responses;
    templates.value = ctx.templates;
    existingCycles.value = ctx.existing_cycles ?? [];
    selectedTemplateId.value ??= ctx.default_template_id ?? null;
}

onMounted(async () => {
    await loadStartContext();
    loading.value = false;
    if (loadError.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.ASSESSMENT_START_VIEWED, {
        template_count: templates.value.length,
        has_default_template: selectedTemplateId.value !== null,
        mode: isSelfMode ? "self_assessment" : "360",
    });
});

async function onCreate() {
    creating.value = true;
    error.value = null;
    try {
        const {
            data,
            error: fetchError,
            statusCode,
        } = await useFetch("/v1/reflections/cycles")
            .post(
                JSON.stringify({
                    template_id: selectedTemplateId.value,
                    is_self_assessment_only: isSelfMode,
                    self_assess_enabled: isSelfMode ? true : selfAssess.value,
                    personal_note: isSelfMode ? "" : personalNote.value,
                }),
                "application/json",
            )
            .json();

        if (fetchError.value) {
            if (statusCode.value === 409) {
                // Someone (another tab, a double-click) started this cycle
                // first; reload so the existing-cycle notice takes over, say
                // so, and move focus to it since the Create button is gone.
                await loadStartContext();
                const cycle = existingCycle.value;
                if (cycle) {
                    recoveryAnnouncement.value = t("reflections.sixty.start.existing.announcement", { name: cycle.template_name, action: continueLabel.value });
                    await nextTick();
                    existingNoticeEl.value?.focus();
                }
                return;
            }
            error.value = (data.value as { error?: string })?.error || t("assessments.start.createError");
            return;
        }

        const cycle = (data.value as { cycle: { id: string; is_self_assessment_only: boolean } }).cycle;

        if (isSelfMode) {
            // Self-assessment cycles are auto-launched by the backend in the
            // same create request, so we go straight into taking the assessment.
            router.visit(`/reflections/360/${cycle.id}/self`);
        } else {
            router.visit(`/reflections/360/${cycle.id}/nominate`);
        }
    } finally {
        creating.value = false;
    }
}
</script>
